import type { Content, TableCell } from 'pdfmake/interfaces';
import type { LinesColumn, TemplateOptions } from '../template-options';
import type { PdfCompanyInfo, PdfClientInfo, PdfSupplierInfo, PdfDocumentLine } from '../types';

export const MM_TO_PT = 2.8346456692913;
export const mm = (v: number) => v * MM_TO_PT;

export function fmtPrice(n: number): string {
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function fmtDate(d?: string | null): string {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

export function interpolate(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? '');
}

export interface RenderData {
  docType: string;
  number: string;
  docDate: string;
  validityDate?: string | null;
  client?: PdfClientInfo;
  supplier?: PdfSupplierInfo;
  lines: PdfDocumentLine[];
  totalHt: number;
  totalDiscount?: number;
  totalVat: number;
  totalTtc: number;
  withholdingAmount: number;
  stampAmount?: number;
  netToPay: number;
  notes: string | null;
}

export interface DesignCtx {
  company: PdfCompanyInfo;
  data: RenderData;
  options: Required<TemplateOptions>;
  docTypeTitle: string;
  logoDataUrl: string | null;
}

export const COLUMN_LABELS: Record<LinesColumn, string> = {
  number: '#',
  articleCode: 'Article',
  description: 'Désignation',
  quantity: 'Qté',
  unit: 'Unité',
  unitPriceHt: 'PU HT',
  discount: 'Rem %',
  vatRate: 'TVA',
  unitPriceTtc: 'Net TTC',
  totalHt: 'Total HT',
  totalTtc: 'Tot. Net TTC',
};

export const COLUMN_WIDTHS: Record<LinesColumn, number | '*'> = {
  number: 22,
  articleCode: 60,
  description: '*',
  quantity: 35,
  unit: 35,
  unitPriceHt: 55,
  discount: 40,
  vatRate: 35,
  unitPriceTtc: 55,
  totalHt: 60,
  totalTtc: 65,
};

export function linesCell(col: LinesColumn, l: PdfDocumentLine): TableCell {
  switch (col) {
    case 'number': return { text: String(l.lineNumber), alignment: 'center' };
    case 'articleCode': return { text: l.articleCode ?? '', alignment: 'left' };
    case 'description': return { text: l.description };
    case 'quantity': return { text: fmtPrice(l.quantity), alignment: 'right' };
    case 'unit': return { text: '', alignment: 'center' };
    case 'unitPriceHt': return { text: fmtPrice(l.unitPriceHt), alignment: 'right' };
    case 'discount': return { text: l.discountPct > 0 ? `${l.discountPct}%` : '—', alignment: 'right' };
    case 'vatRate': return { text: `${l.vatRate}%`, alignment: 'right' };
    case 'unitPriceTtc': return { text: fmtPrice(l.unitPriceHt * (1 - l.discountPct / 100) * (1 + l.vatRate / 100)), alignment: 'right' };
    case 'totalHt': return { text: fmtPrice(l.totalHt), alignment: 'right', bold: true };
    case 'totalTtc': return { text: fmtPrice(l.totalHt * (1 + l.vatRate / 100)), alignment: 'right', bold: true };
    default: return { text: '' };
  }
}

export interface VatBreakdownRow {
  rate: number;
  base: number;
  tax: number;
}

export function computeVatBreakdown(lines: PdfDocumentLine[]): VatBreakdownRow[] {
  const map = new Map<number, { base: number; tax: number }>();
  for (const l of lines) {
    const rate = l.vatRate;
    const existing = map.get(rate) ?? { base: 0, tax: 0 };
    existing.base += l.totalHt;
    existing.tax += l.totalHt * rate / 100;
    map.set(rate, existing);
  }
  return Array.from(map.entries())
    .map(([rate, { base, tax }]) => ({
      rate,
      base: Math.round(base * 1000) / 1000,
      tax: Math.round(tax * 1000) / 1000,
    }))
    .sort((a, b) => b.rate - a.rate);
}

export function partyLines(
  party: PdfClientInfo | PdfSupplierInfo | undefined,
  opts: Required<TemplateOptions>,
): string[] {
  if (!party) return [];
  const out: string[] = [];
  const nameParts: string[] = [];
  if (opts.showPartyCode && party.code) nameParts.push(party.code);
  if (opts.showPartyName && party.name) nameParts.push(party.name);
  if (nameParts.length) out.push(nameParts.join(' — '));
  if (opts.showPartyTaxId && party.taxId) out.push(`MF : ${party.taxId}`);
  if (opts.showPartyAddress && party.address) out.push(party.address);
  if (opts.showPartyCity && party.city) out.push(party.city);
  if (opts.showPartyPhone && party.phone) out.push(`Tél : ${party.phone}`);
  if (opts.showPartyFax && party.fax) out.push(`Fax : ${party.fax}`);
  if ('carRegistrationNumber' in party && opts.showPartyCarRegistration && party.carRegistrationNumber) {
    out.push(`Matricule voiture : ${party.carRegistrationNumber}`);
  }
  return out;
}

export function companyLines(company: PdfCompanyInfo, opts: Required<TemplateOptions>): string[] {
  // Body lines (excluding the heading/name — designs style that separately)
  const out: string[] = [];
  if (opts.showCompanyTagline && company.tagline) out.push(company.tagline);
  if (opts.showCompanySubtitle && company.subtitle) out.push(company.subtitle);
  if (opts.showCompanyTaxId && company.taxId) out.push(`MF : ${company.taxId}`);
  if (opts.showCompanyAddress && company.address) out.push(company.address);
  if (opts.showCompanyPhone && company.phone) out.push(`Tél : ${company.phone}`);
  if (opts.showCompanyFax && company.fax) out.push(`Fax : ${company.fax}`);
  if (opts.showCompanyEmail && company.email) out.push(company.email);
  return out;
}

export function companySocialsLine(company: PdfCompanyInfo, opts: Required<TemplateOptions>): string | null {
  const parts: string[] = [];
  if (opts.showCompanyWebsite && company.website) parts.push(`🌐 ${company.website}`);
  if (opts.showCompanySocials) {
    if (company.socialFacebook) parts.push(`f ${company.socialFacebook}`);
    if (company.socialInstagram) parts.push(`@ ${company.socialInstagram}`);
    if (company.socialLinkedin) parts.push(`in ${company.socialLinkedin}`);
  }
  return parts.length > 0 ? parts.join('   ·   ') : null;
}

export function logoBlock(ctx: DesignCtx): Content | null {
  if (ctx.options.logoPosition === 'hidden' || !ctx.logoDataUrl) return null;
  const s = mm(ctx.options.logoSizeMm);
  return { image: ctx.logoDataUrl, width: s, height: s, fit: [s, s] };
}

export function totalsRows(ctx: DesignCtx): TableCell[][] {
  const { options: o, data } = ctx;
  const rows: TableCell[][] = [];
  if (o.showTotalHt) rows.push([{ text: 'Total HT', alignment: 'right' }, { text: `${fmtPrice(data.totalHt)} DT`, alignment: 'right', bold: true }]);
  if (o.showTotalDiscount && (data.totalDiscount ?? 0) > 0) rows.push([{ text: 'Total remises', alignment: 'right' }, { text: `-${fmtPrice(data.totalDiscount ?? 0)} DT`, alignment: 'right' }]);
  if (o.showTotalVat) rows.push([{ text: 'Total TVA', alignment: 'right' }, { text: `${fmtPrice(data.totalVat)} DT`, alignment: 'right' }]);
  if (o.showTotalTtc) rows.push([{ text: 'Total TTC', alignment: 'right', bold: true }, { text: `${fmtPrice(data.totalTtc)} DT`, alignment: 'right', bold: true }]);
  if (o.showWithholding && (data.withholdingAmount ?? 0) > 0) rows.push([{ text: 'Retenue à la source', alignment: 'right' }, { text: `-${fmtPrice(data.withholdingAmount)} DT`, alignment: 'right' }]);
  if (o.showStamp && (data.stampAmount ?? 0) > 0) rows.push([{ text: 'Timbre fiscal', alignment: 'right' }, { text: `${fmtPrice(data.stampAmount ?? 0)} DT`, alignment: 'right' }]);
  if (o.showNetToPay && (data.netToPay ?? 0) > 0) rows.push([{ text: 'Net à payer', alignment: 'right', bold: true }, { text: `${fmtPrice(data.netToPay)} DT`, alignment: 'right', bold: true }]);
  return rows;
}

export function buildVars(ctx: DesignCtx): Record<string, string> {
  const c = ctx.company;
  return {
    companyName: c.companyName,
    taxId: c.taxId ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    address: c.address ?? '',
    website: c.website ?? '',
    fax: c.fax ?? '',
    rib: c.rib ?? '',
    bankName: c.bankName ?? '',
    tagline: c.tagline ?? '',
    subtitle: c.subtitle ?? '',
    docNumber: ctx.data.number,
    docDate: fmtDate(ctx.data.docDate),
    clientName: ctx.data.client?.name ?? '',
    supplierName: ctx.data.supplier?.name ?? '',
    totalHt: fmtPrice(ctx.data.totalHt),
    totalTtc: fmtPrice(ctx.data.totalTtc),
    docTypeTitle: ctx.docTypeTitle,
  };
}
